import React, { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export interface Modal2Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  subtitle?: React.ReactNode;
  children: ReactNode;

  /* ---- Sizing ---- */
  /** Figma pixel presets: xs 400 / sm 480 / md 520 / lg 560 / xl 600 / 2xl 640 / 3xl 680 / full. */
  size?: string;
  /** Explicit max width (overrides `size`). e.g. width={560} → width:100%; max-width:560px */
  width?: number | string;
  /** Optional fixed height; body scrolls inside. e.g. height={600} */
  height?: number | string;
  /** Max height (default "88vh" per Figma). */
  maxHeight?: number | string;
  /** Optional min width. */
  minWidth?: number | string;

  /* ---- Structure / behavior ---- */
  showHeader?: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  centered?: boolean;
  closeIcon?: ReactNode;
  headerPadding?: boolean;
  bodyPadding?: boolean;
  footerPadding?: boolean;
  loading?: boolean;
  loadingText?: string;
  disableBodyScroll?: boolean;
  destroyOnClose?: boolean;
  onAfterClose?: () => void;

  /* ---- Styling ---- */
  zIndex?: number;
  maskClassName?: string;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  footer?: ReactNode;

  /* ---- A11y ---- */
  ariaLabel?: string;
  role?: string;
}

const SIZE_CLASSES: Record<string, string> = {
  xs: "max-w-[400px]",
  sm: "max-w-[480px]",
  md: "max-w-[520px]",
  lg: "max-w-[560px]",
  xl: "max-w-[600px]",
  "2xl": "max-w-[640px]",
  "3xl": "max-w-[680px]",
  full: "max-w-full",
};

const DEFAULT_SIZE_CLASS = SIZE_CLASSES.md;

const Modal2: React.FC<Modal2Props> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size,
  width,
  height,
  maxHeight = "88vh",
  minWidth,
  showHeader = true,
  closable = true,
  maskClosable = true,
  centered = true,
  closeIcon,
  headerPadding = true,
  bodyPadding = true,
  footerPadding = true,
  loading = false,
  loadingText = "Loading...",
  disableBodyScroll = false,
  destroyOnClose = true,
  onAfterClose,
  zIndex = 100,
  maskClassName = "",
  className = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  footer,
  ariaLabel,
  role = "dialog",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const prevOpen = useRef(isOpen);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      if (!disableBodyScroll) {
        document.body.style.overflow = "hidden";
      }
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (!disableBodyScroll) {
        document.body.style.overflow = "unset";
      }
    };
  }, [isOpen, closable, onClose, disableBodyScroll]);

  useEffect(() => {
    if (prevOpen.current && !isOpen) {
      onAfterClose?.();
    }
    prevOpen.current = isOpen;
  }, [isOpen, onAfterClose]);

  if (!isOpen && destroyOnClose) return null;

  const handleMaskClick = (e: React.MouseEvent) => {
    if (maskClosable && e.target === e.currentTarget) {
      onClose();
    }
  };

  const widthClass =
    width === undefined ? SIZE_CLASSES[size || "md"] || DEFAULT_SIZE_CLASS : "max-w-full";

  const panelStyle: React.CSSProperties = {
    maxHeight: height !== undefined ? height : maxHeight,
    ...(height !== undefined ? { height } : {}),
    ...(width !== undefined ? { width: "100%", maxWidth: width } : {}),
    ...(minWidth !== undefined ? { minWidth } : {}),
  };

  return createPortal(
    <div
      className={`fixed inset-0 bg-[var(--color-mask)] flex ${
        centered ? "items-center justify-center" : "items-start justify-center pt-10 sm:pt-16"
      } p-3 sm:p-6 ${!isOpen ? "invisible" : ""} ${maskClassName}`}
      style={{ zIndex }}
      onClick={handleMaskClick}
    >
      <div
        ref={modalRef}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === "string" ? title : undefined)}
        className={`
          relative bg-[var(--color-bg-base)] rounded-[20px] shadow-xl w-full ${widthClass}
          max-h-[88vh] flex flex-col
          ${className}
          ${loading ? "pointer-events-none" : ""}
        `}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="absolute inset-0 bg-[var(--color-bg-base)] bg-opacity-75 flex items-center justify-center z-10 rounded-[20px]">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-primary-600)]"></div>
              <span className="text-[var(--color-text-secondary)]">{loadingText}</span>
            </div>
          </div>
        )}

        {showHeader && (title || closable) && (
          <div
            className={`flex items-center justify-between gap-3 ${
              headerPadding ? "px-4 sm:px-6 pt-[18px] sm:pt-[22px] pb-[18px]" : "p-0"
            } border-b border-[var(--color-border-tertiary)] ${headerClassName}`}
          >
            <div className="flex-1 min-w-0 pr-2">
              {title && (
                <div className="text-[18px] font-extrabold tracking-[-0.02em] text-[var(--color-text-primary)] leading-tight">
                  {title}
                </div>
              )}
              {subtitle && (
                <div className="text-[12.5px] text-[var(--color-text-tertiary)] mt-[4px] leading-snug">
                  {subtitle}
                </div>
              )}
            </div>
            {closable && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-[38px] h-[38px] flex-none grid place-items-center rounded-[11px] bg-[var(--color-bg-muted)] hover:bg-[var(--color-layout-sidebar-collapse-hover)] text-[var(--color-text-secondary)] transition-colors"
                disabled={loading}
              >
                {closeIcon ?? <X className="w-[18px] h-[18px]" />}
              </button>
            )}
          </div>
        )}

        <div
          className={`flex-1 ${disableBodyScroll ? "overflow-hidden" : "overflow-y-auto"} ${
            bodyPadding ? "p-4 sm:p-6" : "p-0"
          } ${bodyClassName}`}
        >
          {children}
        </div>

        {footer && (
          <div
            className={`${
              footerPadding ? "px-4 sm:px-6 py-[18px]" : "p-0"
            } border-t border-[var(--color-border-tertiary)] ${footerClassName}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export const Modal2Footer: React.FC<{
  children: ReactNode;
  className?: string;
  justify?: "start" | "center" | "end" | "between";
}> = ({ children, className = "", justify = "end" }) => {
  const justifyClasses = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
  };

  return (
    <div className={`flex items-center gap-2.5 ${justifyClasses[justify]} ${className}`}>
      {children}
    </div>
  );
};

export const Modal2Button: React.FC<{
  children: ReactNode;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  className?: string;
  form?: string;
}> = ({
  children,
  variant = "secondary",
  size = "md",
  loading = false,
  disabled = false,
  onClick,
  type = "button",
  className = "",
  form,
}) => {
  const variantClasses = {
    primary:
      "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:-translate-y-0.5 hover:shadow-[var(--shadow-brand)]",
    secondary:
      "bg-white border border-[var(--color-layout-dropdown-badge-border)] text-[var(--color-text-strong)] hover:bg-[var(--color-bg-muted)]",
    danger:
      "bg-[var(--color-error-dark)] text-white hover:bg-[var(--color-error)]",
    ghost: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]",
  };

  const sizeClasses = {
    sm: "h-9 px-4 text-[13px]",
    md: "h-11 px-5 text-[13.5px]",
    lg: "h-12 px-6 text-sm",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      form={form}
      className={`
        inline-flex items-center justify-center gap-2 rounded-[9px] font-semibold
        transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${disabled || loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
        ${className}
      `}
    >
      {loading && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
      )}
      {children}
    </button>
  );
};

export default Modal2;